import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const SuccessStories = () => {
  const [currentStory, setCurrentStory] = useState(0);

  const stories = [
  {
    id: 1,
    name: "Sarah Johnson",
    country: "United States",
    program: "HSK 6 Preparation Program",
    university: "Peking University",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_103b528db-1763293982935.png",
    imageAlt: "Professional headshot of young woman with long brown hair wearing white blouse smiling confidently at camera",
    quote: "ChineseLangApp made my application process incredibly smooth. Within 3 months, I was accepted into my dream program at Peking University. The platform's guidance was invaluable.",
    achievement: "Achieved HSK 6 certification",
    year: "2025"
  },
  {
    id: 2,
    name: "Mohammed Al-Rashid",
    country: "Saudi Arabia",
    program: "Business Chinese Program",
    university: "Fudan University",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12039f805-1763295833472.png",
    imageAlt: "Professional portrait of Middle Eastern man with short dark hair in navy blue suit with warm smile",
    quote: "The comprehensive institution database helped me find the perfect program that aligned with my career goals. Now I\'m fluent in business Chinese and working with international companies.",
    achievement: "Secured position at Fortune 500 company",
    year: "2024"
  },
  {
    id: 3,
    name: "Elena Rodriguez",
    country: "Spain",
    program: "Intensive Chinese Language Course",
    university: "Tsinghua University",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1db784668-1763301780770.png",
    imageAlt: "Headshot of Hispanic woman with dark wavy hair wearing professional gray blazer with confident expression",
    quote: "From application to acceptance, everything was transparent and well-organized. The document management system saved me so much time. Highly recommend ChineseLangApp!",
    achievement: "Completed program with honors",
    year: "2025"
  },
  {
    id: 4,
    name: "David Kim",
    country: "South Korea",
    program: "Chinese Culture & Language",
    university: "Shanghai Jiao Tong University",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11e69798f-1763293582771.png",
    imageAlt: "Professional photo of Asian man with short black hair wearing white dress shirt with friendly smile",
    quote: "The platform\'s user-friendly interface and excellent customer support made my transition to studying in China seamless. I couldn\'t have asked for a better experience.",
    achievement: "Published research on Chinese linguistics",
    year: "2024"
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStory((prev) => (prev + 1) % stories?.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [stories?.length]);

  const handlePrevious = () => {
    setCurrentStory((prev) => (prev - 1 + stories?.length) % stories?.length);
  };

  const handleNext = () => {
    setCurrentStory((prev) => (prev + 1) % stories?.length);
  };

  const story = stories?.[currentStory];

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-6 md:p-8 lg:p-10 border border-primary/10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/20 rounded-full flex items-center justify-center">
            <Icon name="Award" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Success Stories
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              Real experiences from our students
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="mb-6">
            <div className="w-24 h-24 md:w-28 md:h-28 mx-auto mb-4 rounded-full overflow-hidden border-4 border-primary/20">
              <Image
                src={story?.image}
                alt={story?.imageAlt}
                className="w-full h-full object-cover" />

            </div>

            <div className="text-center mb-4">
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-1">
                {story?.name}
              </h3>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground font-caption mb-2">
                <Icon name="MapPin" size={16} />
                <span>{story?.country}</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
                <Icon name="GraduationCap" size={14} color="var(--color-primary)" />
                <span className="text-xs font-medium text-primary font-caption">
                  {story?.university}
                </span>
              </div>
            </div>

            <div className="relative mb-4">
              <Icon
                name="Quote"
                size={32}
                color="var(--color-accent)"
                className="absolute -top-2 -left-2 opacity-30" />

              <p className="text-sm md:text-base text-foreground/90 italic leading-relaxed px-4">
                "{story?.quote}"
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div>
                <p className="text-xs text-muted-foreground font-caption mb-1">
                  Program
                </p>
                <p className="text-sm font-medium text-foreground">
                  {story?.program}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground font-caption mb-1">
                  Achievement
                </p>
                <p className="text-sm font-medium text-success">
                  {story?.achievement}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-card hover:bg-muted border border-border transition-smooth"
              aria-label="Previous story">

              <Icon name="ChevronLeft" size={20} color="var(--color-foreground)" />
            </button>

            <div className="flex gap-2">
              {stories?.map((_, index) =>
              <button
                key={index}
                onClick={() => setCurrentStory(index)}
                className={`w-2 h-2 rounded-full transition-smooth ${
                index === currentStory ?
                'bg-primary w-6' : 'bg-muted-foreground/30'}`
                }
                aria-label={`Go to story ${index + 1}`} />

              )}
            </div>

            <button
              onClick={handleNext}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-card hover:bg-muted border border-border transition-smooth"
              aria-label="Next story">

              <Icon name="ChevronRight" size={20} color="var(--color-foreground)" />
            </button>
          </div>
        </div>
      </div>
      <div className="mt-6 bg-card rounded-lg p-4 md:p-6 shadow-warm border border-border">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
          University Partnerships
        </h3>
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {[
          { name: "Peking University", students: "2,500+" },
          { name: "Tsinghua University", students: "2,200+" },
          { name: "Fudan University", students: "1,800+" },
          { name: "Shanghai Jiao Tong", students: "1,600+" }]?.
          map((partner, index) =>
          <div
            key={index}
            className="flex items-center gap-2 p-3 bg-muted/50 rounded-md">

              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Building2" size={16} color="var(--color-primary)" />
              </div>
              <div className="min-w-0">
                <p className="text-xs md:text-sm font-medium text-foreground truncate">
                  {partner?.name}
                </p>
                <p className="text-xs text-muted-foreground font-caption">
                  {partner?.students} students
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

};

export default SuccessStories;